import React from 'react';
import { StandardTableActions } from '../../../components/ui';

import { Column } from '../../../components/DataTable/DataTable';
import { CallType } from './useData';
import { FieldConfig } from '../../../components/generic/fieldRenderers';
import { FormLayout } from '../../../components/generic/FormLayoutRenderer';

export const moduleConfig = {
  module: {
    title: 'Call Types',
    description: 'Manage call types',
    permissionModuleKey: 'call_type',
    route: {
      list: '/call_type',
      new: '/call_type/new',
      detail: (id: number) => `/call_type/${id}`,
      edit: (id: number) => `/call_type/${id}/edit`,
    },
    api: {
      list: '/call-type',
      detail: (id: number) => `/call-type/${id}`,
    },
  },
  pagination: {
    initialItemsPerPage: 10,
    itemsPerPageOptions: [10, 25, 50, 100],
  },
  search: {
    placeholder: 'Search call types...',
  },
  filterGroups: [
    {
      name: 'basic',
      label: 'Basic',
      icon: '🔍',
      fields: ['call_type_desc'],
    },
  ],
  filters: {
    fields: [
      {
        key: 'call_type_desc',
        label: 'Description',
        placeholder: 'Type to filter description...',
        type: 'text' as const,
        getValue: (item: CallType) => item.call_type_desc,
        options: [],
      },
    ],
    applyFilter: (items: CallType[], filters: { call_type_desc: string[] | string }) => {
      return items.filter((item) => {
        const descrFilter = filters.call_type_desc;
        if (typeof descrFilter === 'string' && descrFilter.trim() !== '') {
          const value = (item.call_type_desc || '').toLowerCase();
          if (!value.includes(descrFilter.toLowerCase())) return false;
        } else if (Array.isArray(descrFilter) && descrFilter.length > 0) {
          const value = (item.call_type_desc || '').toLowerCase();
          const matches = descrFilter.some((filter) => {
            const filterLower = filter.toLowerCase();
            return value.includes(filterLower) || filterLower.includes(value);
          });
          if (!matches) return false;
        }
        return true;
      });
    },
  },
  toolbar: {
    actions: [
      {
        key: 'add',
        icon: 'add',
        title: 'Add New Call Type',
        showTitle: false,
        variant: 'primary' as const,
        href: '/call_type/new',
      },
    ],
  },
  table: {
    actionsPosition: 'left' as const,
    onRowClick: (item: CallType) => {
      window.location.href = `/call_type/${item.id}`;
    },
    keyExtractor: (item: CallType) => item.id,
  },
  emptyStates: {
    noData: 'Get started by creating a new call type.',
    noSearchResults: 'No call types found matching your search criteria.',
    noFilterResults: 'No call types match the selected filters.',
  },
  delete: {
    title: 'Delete Call Type',
    message: 'Are you sure you want to delete this call type? This action cannot be undone.',
    confirmText: 'Delete',
    cancelText: 'Cancel',
    errorMessage: 'Failed to delete call type. Please try again.',
    handler: async (id: number) => {
      const { deleteCallType } = await import('../../../services/mutations');
      await deleteCallType(id);
    },
  },
  utils: {
    formatDate: (dateString?: string) => {
      if (!dateString) return '-';
      return new Date(dateString).toLocaleDateString();
    },
  },
  detail: {
    subtitle: 'Call Type Details',
    notFoundTitle: 'Call Type not found',
    notFoundMessage: 'The call type you\'re looking for doesn\'t exist.',
    notFoundBackText: 'Back to Call Types',
    defaultTitle: 'Unnamed Call Type',
    getTitle: (item: CallType) => {
      return item.call_type_desc || `Call Type #${item.id}`;
    },
  },
  form: {
    new: {
      title: 'New Call Type',
      subtitle: 'Add a new call type',
      submitText: 'Create Call Type',
    },
    edit: {
      title: 'Edit Call Type',
      subtitle: 'Update call type information',
      submitText: 'Update Call Type',
    },
    cancelText: 'Cancel',
    loadingText: 'Loading call type...',
    errorMessage: 'Failed to save call type. Please try again.',
    create: async (data: Partial<CallType>) => {
      const { createCallType } = await import('../../../services/mutations');
      return await createCallType(data);
    },
    update: async (id: number, data: Partial<CallType>) => {
      const { updateCallType } = await import('../../../services/mutations');
      return await updateCallType(id, data);
    },
  },
  fields: [
    {
      name: 'call_type_desc',
      label: 'Description',
      type: 'text',
      required: true,
      placeholder: 'Enter call type description',
      validation: {
        required: true,
        minLength: 1,
        maxLength: 255,
      },
    },
  ] as FieldConfig[],
  formLayout: {
    mode: 'default',
    sections: [
      {
        id: 'basic',
        title: 'Call Type Information',
        icon: '📝',
        fields: ['call_type_desc'],
        columns: 1,
        collapsible: false,
      },
    ],
  } as FormLayout,
};

export const getColumns = (formatDate: (dateString?: string) => string): Column<CallType>[] => [
  {
    key: 'id',
    header: 'ID',
    sortable: true,
    render: (value) => (
      <span className="text-sm text-gray-900">{value || '-'}</span>
    ),
  },
  {
    key: 'call_type_desc',
    header: 'Description',
    sortable: true,
    render: (value) => (
      <span className="text-sm text-gray-900">{value || '-'}</span>
    ),
  },
];

export const getDetailSections = (
  item: CallType,
  formatDate: (dateString?: string) => string
) => {
  return [
    {
      title: 'Basic Information',
      icon: '📝',
      fields: [
        {
          label: 'Description',
          value: item.call_type_desc || '-',
        },
      ],
    },
  ];
};

export const getStatusBadges = (item: CallType) => {
  return null;
};

export const getInitialFormData = (): Partial<CallType> => {
  return {
    call_type_desc: '',
  };
};

export const mapItemToFormData = (item: CallType): Partial<CallType> => {
  return {
    call_type_desc: item.call_type_desc || '',
  };
};

export const getToolbarIcon = (iconName: string): React.ReactNode => {
  switch (iconName) {
    case 'add':
      return (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
        </svg>
      );
    default:
      return null;
  }
};

export const getTableActions = (
  item: CallType,
  onDelete: (id: number) => void,
  _colors?: unknown,
  _listContext?: unknown,
  options?: { canDelete?: boolean; canEdit?: boolean }
): React.ReactNode => {
  const canDelete = options?.canDelete !== false;
  const canEdit = options?.canEdit !== false;
  return (
    <StandardTableActions
      viewTo={moduleConfig.module.route.detail(item.id)}
      editTo={moduleConfig.module.route.edit(item.id)}
      onDelete={() => onDelete(item.id)}
      canEdit={canEdit}
      canDelete={canDelete}
    />
  );
};

